/**
 * @file Wire shapes for the admin → hypervisor read proxy (umbrella
 *       #883, part #891).
 * @module @labcraft/admin/shared/hypervisor-routes
 *
 * The admin daemon has no live connection to the hypervisor daemon
 * today, so `GET /api/hypervisor` reports `connected: false` and an
 * empty machine list. The lifecycle enum is imported from
 * `@labcraft/hypervisor/shared/subscriptions` (umbrella #1048 /
 * #1050) — the pre-#1050 hand-mirror here had already silently
 * dropped the `failed` state, and the "would pull the Firecracker
 * backend into the closure" rationale was false (the shared module
 * carries only Zod schemas, and admin already imports
 * `@labcraft/hypervisor/settings`). The proxy still validates the
 * downstream payload at the boundary, so a `Machine` wire-shape drift
 * fails loud at parse time.
 *
 * `state` carries each machine's live lifecycle state. It was reserved
 * (optional, unpopulated) by part #891 so the table's state pill could
 * light up without a wire change once the hypervisor exposed it; that
 * exposure landed in umbrella #1258 Part H / #1337, which added `state`
 * to the hypervisor's own `Machine` wire shape. The proxy's structural
 * pass-through (`Machine` is structurally an `AdminMachine`) now fills
 * this field in with no admin-side mapping. It stays optional because a
 * `Machine` mid-drain (a `terminated` session) omits it.
 *
 * This module also carries the admin control-plane command surface: the
 * operator-suspend route (#1337) and the force-expire-pin route (#1340),
 * plus the fleet-lifecycle subscription (`fleet_subscription.ts`) that
 * keeps the panel fresh. Resume/boot are deliberately absent — the admin
 * command set is a strict subset of owner operations (core invariant 1).
 */

import { defineRoute, CoreString } from '@labcraft/core'
import { z } from 'zod'
import { MachineLifecycleStateSchema } from '@labcraft/hypervisor/shared/subscriptions'

/**
 * Machine lifecycle state — the hypervisor's wire enum. Maps to a
 * `.spill--*` status pill in the panel.
 */
export { MachineLifecycleStateSchema }

/** Parsed {@link MachineLifecycleStateSchema}. */
export type MachineLifecycleState = z.infer<typeof MachineLifecycleStateSchema>

/**
 * The engagement's overlay-stack pin as the admin panel mirror sees it
 * (umbrella #1258 Part D / #1333 + Part H / #1340). Redeclared here (not
 * imported from `@labcraft/hypervisor/shared/routes`) under the same
 * boundary-mirror discipline as {@link AdminMachineSchema} — the wire
 * `Machine.pin` passes through the proxy structurally into this shape. The
 * panel renders the trailing content layer as the "pinned content version"
 * and flags `stale` pins (a next-resume clean-refresh).
 *
 * @property layers - Ordered overlay layer refs, base-first (`layers[0]`
 *   the base floor; trailing entries the pinned content layers).
 * @property stackFingerprint - Fingerprint over the ordered stack, or
 *   `null` for dev layers with no `SHA256SUMS`.
 * @property stale - Whether the pin is force-expired / security-expired
 *   (its next resume clean-refreshes onto current content).
 */
export const AdminMachinePinSchema = z.object({
  layers: z.array(z.string().min(1)),
  stackFingerprint: z.string().nullable(),
  stale: z.boolean(),
})

/** Parsed {@link AdminMachinePinSchema}. */
export type AdminMachinePin = z.infer<typeof AdminMachinePinSchema>

/**
 * The learner an engagement machine belongs to, as the admin panel shows
 * it. Resolved admin-side by the proxy from the machine id's embedded
 * learner id against `@labcraft/account`'s `getLearner` — NOT a hypervisor
 * wire field. Optional because a machine whose id carries no learner, or
 * whose lookup misses (a stale VM whose learner row was deleted), omits it.
 *
 * @property id - The learner's account id (parsed from the machine id).
 * @property displayName - The learner's preferred display name.
 * @property email - The learner's verified email.
 */
export const AdminMachineLearnerSchema = z.object({
  id: z.string().min(1),
  displayName: z.string(),
  email: z.string(),
})

/** Parsed {@link AdminMachineLearnerSchema}. */
export type AdminMachineLearner = z.infer<typeof AdminMachineLearnerSchema>

/**
 * One hypervisor machine as the admin panel sees it — the
 * `@labcraft/hypervisor` wire `Machine` plus its lifecycle `state`,
 * engagement `pin`, and (admin-enriched) `learner`. `state` is populated
 * from the hypervisor's live `MachineProvider.currentLifecycleState()`
 * (#1337) and `pin` from `Firecracker.pinFor()` (#1340) via the proxy's
 * structural pass-through; `learner` is resolved admin-side against
 * `@labcraft/account` (not a wire field). All three optional because a
 * `Machine` mid-drain / pin-less / learner-less session omits them.
 */
export const AdminMachineSchema = z.object({
  id: z.string().min(1),
  kind: z.string().min(1),
  name: z.string().min(1),
  hostIp: z.string().min(1),
  guestIp: z.string().min(1),
  tap: z.string().min(1),
  ptyId: z.string().min(1),
  attached: z.boolean(),
  state: MachineLifecycleStateSchema.optional(),
  pin: AdminMachinePinSchema.optional(),
  learner: AdminMachineLearnerSchema.optional(),
})

/** Parsed {@link AdminMachineSchema}. */
export type AdminMachine = z.infer<typeof AdminMachineSchema>

/**
 * Response for `GET /api/hypervisor`.
 *
 * @property connected - Whether the admin daemon has a live hypervisor
 *   connection. `false` until one is configured — the panel renders an
 *   honest "not connected yet" surface in that case.
 * @property machines - The booted machines (empty when not connected).
 */
export const HypervisorListResponseSchema = z.object({
  connected: z.boolean(),
  machines: z.array(AdminMachineSchema),
})

/** Parsed {@link HypervisorListResponseSchema}. */
export type HypervisorListResponse = z.infer<
  typeof HypervisorListResponseSchema
>

/** `GET /api/hypervisor` — admin-gated machine list (proxy). */
export const ListHypervisorMachinesRoute = defineRoute({
  method: 'GET',
  path: '/api/hypervisor',
  response: HypervisorListResponseSchema,
  description:
    'List hypervisor machines (admin-gated). connected=false until an admin→hypervisor connection is wired.',
})

/** Path params for `POST /api/hypervisor/:id/operator-suspend`. */
export const OperatorSuspendMachineParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed {@link OperatorSuspendMachineParamsSchema}. */
export type OperatorSuspendMachineParams = z.infer<
  typeof OperatorSuspendMachineParamsSchema
>

/**
 * `POST /api/hypervisor/:id/operator-suspend` — admin-gated operator
 * suspend (umbrella #1258 Part H / #1337). The proxy forwards it to the
 * hypervisor's own `POST /hypervisor/:id/operator-suspend`, which drives
 * the reclaim path (running → pausing → paused → snapshotting → saved)
 * and stamps the `operator-suspend` lifecycle reason. This is the ONLY
 * mutating command the admin control plane exposes on a machine — resume
 * and boot are intentionally absent (the admin command set is a strict
 * subset of owner operations, core invariant 1). Idempotent on
 * already-suspended / unknown ids.
 */
export const OperatorSuspendMachineRoute = defineRoute({
  method: 'POST',
  path: '/api/hypervisor/:id/operator-suspend',
  params: OperatorSuspendMachineParamsSchema,
  response: z.void(),
  description: CoreString.oneLine`
    Admin-gated operator suspend: proxy the reclaim command to the hypervisor,
    which snapshots the running Machine and stamps the operator-suspend reason.
    Idempotent on already-suspended / unknown ids.
  `,
})

/** Path params for `POST /api/hypervisor/:id/force-expire-pin`. */
export const ForceExpirePinMachineParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed {@link ForceExpirePinMachineParamsSchema}. */
export type ForceExpirePinMachineParams = z.infer<
  typeof ForceExpirePinMachineParamsSchema
>

/**
 * `POST /api/hypervisor/:id/force-expire-pin` — admin-gated pin
 * force-expiry (umbrella #1258 Part H / #1340). The proxy forwards it to
 * the hypervisor's own `POST /hypervisor/:id/force-expire-pin`, which
 * flags one VM's engagement pin unavailable so its next resume
 * clean-refreshes onto CURRENT content (`content-refreshed`, #1333)
 * instead of restoring the snapshot. A bounded admin command (a strict
 * subset of owner operations, core invariant 1) — it only flags a pin,
 * never resumes / boots; the owner is the gate. Idempotent on
 * already-flagged / pin-less / unknown ids.
 */
export const ForceExpirePinMachineRoute = defineRoute({
  method: 'POST',
  path: '/api/hypervisor/:id/force-expire-pin',
  params: ForceExpirePinMachineParamsSchema,
  response: z.void(),
  description: CoreString.oneLine`
    Admin-gated pin force-expiry: proxy the command to the hypervisor, which
    flags one VM's engagement pin so its next resume clean-refreshes onto
    current content instead of restoring. Idempotent on already-flagged /
    unknown ids.
  `,
})
